import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, BookOpen, Clock, CheckCircle, AlertCircle,
    BookMarked, Layers, Tag, Info, Users,
    ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';

// ─── Brand colour tokens ─────────────────────────────────────────────────────
const C = {
    green: '#22c55e',
    greenDark: '#15803d',
    greenMid: '#16a34a',
    green50: '#f0fdf4',
    green100: '#dcfce7',
    blue: '#3b82f6',
    blueDark: '#0B2033',
    blue50: '#eff6ff',
    blue100: '#dbeafe',
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',
    yellow: '#f59e0b',
    yellowLight: '#fef9c3',
    yellowDark: '#854d0e',
    red: '#ef4444',
    redLight: '#fee2e2',
    heroBg: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #15803d 100%)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const capitalizeValue = (value) => {
    if (!value) return '';
    const special = {
        soft_skills: 'Soft Skills', all_employees: 'All Employees',
        super_admin: 'Super Admin', hr_manager: 'HR Manager',
        hr_executive: 'HR Executive', sales_manager: 'Sales Manager',
        sales_executive: 'Sales Executive', dispatch_manager: 'Dispatch Manager',
        support_manager: 'Support Manager',
    };
    return special[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

const formatDescriptionText = (text) => {
    if (!text || typeof text !== 'string') return 'No description provided';
    const cleaned = text.trim();
    if (!cleaned) return 'No description provided';
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0s';
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0) parts.push(`${secs}s`);
    return parts.length > 0 ? parts.join(' ') : '0s';
};

const getPathDurationInMinutes = (path) => {
    if (!path) return 0;
    const { totalCourseDurationMinutes, courses, estimatedDuration } = path;
    if (totalCourseDurationMinutes !== undefined && totalCourseDurationMinutes !== null) {
        const m = Number(totalCourseDurationMinutes);
        if (!Number.isNaN(m)) return m;
    }
    if (Array.isArray(courses) && courses.length > 0) {
        return courses.reduce((t, c) => {
            const d = Number(c?.duration) || Number(c?.course?.duration) || 0;
            return Number.isNaN(d) ? t : t + d;
        }, 0);
    }
    const h = Number(estimatedDuration);
    return !Number.isNaN(h) ? h * 60 : 0;
};

const STATUS_CONFIG = {
    published: { label: 'Published', classes: 'bg-blue-100 text-blue-800' },
    draft: { label: 'Draft', classes: 'bg-gray-100 text-gray-700' },
    archived: { label: 'Archived', classes: 'bg-gray-100 text-gray-700' },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const HeroBadge = ({ icon: Icon, label, value }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 12, padding: '12px 16px',
    }}>
        <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.15)',
        }}>
            <Icon size={16} color="#fff" />
        </div>
        <div>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</p>
            <p style={{ fontSize: 13, color: '#fff', fontWeight: 700, margin: 0 }}>{value ?? '—'}</p>
        </div>
    </div>
);

const SectionCard = ({ title, icon: Icon, children, accent = C.green }) => (
    <div style={{
        background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 20,
    }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '15px 20px', borderBottom: `1px solid ${C.gray100}`,
            background: C.gray50,
        }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${accent}18` }}>
                <Icon size={15} color={accent} />
            </div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: C.gray800, margin: 0 }}>{title}</h3>
        </div>
        <div style={{ padding: '18px 20px' }}>{children}</div>
    </div>
);

const CourseCard = ({ courseEntry, index, onViewCourse }) => {
    const [expanded, setExpanded] = useState(false);
    const courseData = courseEntry.course || courseEntry;
    const durationMinutes = Number(courseData?.duration) || Number(courseEntry.duration) || 0;
    // Resolve the course ID from either a populated object or a plain ID string
    const courseId = courseData?._id || courseData?.id ||
        (typeof courseEntry.course === 'string' ? courseEntry.course : null);

    return (
        <div style={{
            border: `1px solid ${expanded ? C.green : C.gray200}`,
            borderRadius: 10, overflow: 'hidden',
            boxShadow: expanded ? `0 4px 14px rgba(34,197,94,0.10)` : '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s',
            background: '#fff',
        }}>
            <button
                onClick={() => setExpanded(v => !v)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '13px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: expanded ? C.green50 : '#fff', transition: 'background 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green : C.green100,
                        color: expanded ? '#fff' : C.greenMid,
                        fontSize: 13, fontWeight: 700, transition: 'all 0.2s',
                    }}>
                        {index + 1}
                    </div>
                    <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>
                            {courseData?.title || courseEntry.title || 'Untitled Course'}
                        </p>
                        <p style={{ fontSize: 11, color: C.gray500, margin: '2px 0 0' }}>
                            {capitalizeValue(courseData?.level || 'Beginner')} · {capitalizeValue(courseData?.category || 'General')}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {durationMinutes > 0 && (
                        <span style={{ fontSize: 12, color: C.gray500, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={12} /> {formatDuration(durationMinutes)}
                        </span>
                    )}
                    <div style={{
                        width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green100 : C.gray100,
                    }}>
                        {expanded ? <ChevronUp size={13} color={C.greenMid} /> : <ChevronDown size={13} color={C.gray500} />}
                    </div>
                </div>
            </button>
            {expanded && (
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.green100}`, background: C.green50 }}>
                    {courseData?.description ? (
                        <p style={{ fontSize: 12, color: C.gray600, lineHeight: 1.6, margin: 0 }}>
                            {courseData.description}
                        </p>
                    ) : (
                        <p style={{ fontSize: 12, color: C.gray400, fontStyle: 'italic', margin: 0 }}>
                            No course description available.
                        </p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
                        {courseData?.status && (
                            <span style={{
                                padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                background: courseData.status === 'published' ? C.green100 : C.gray100,
                                color: courseData.status === 'published' ? C.greenMid : C.gray600,
                            }}>
                                {capitalizeValue(courseData.status)}
                            </span>
                        )}
                        {courseEntry.isRequired !== undefined && (
                            <span style={{
                                padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                background: courseEntry.isRequired ? C.blue100 : C.gray100,
                                color: courseEntry.isRequired ? C.blueDark : C.gray500,
                            }}>
                                {courseEntry.isRequired ? 'Required' : 'Optional'}
                            </span>
                        )}

                        {/* View Course button – only render if we have an ID */}
                        {courseId && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onViewCourse(courseId); }}
                                style={{
                                    marginLeft: 'auto',
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                    background: C.blue, color: '#fff', fontSize: 11, fontWeight: 600,
                                    transition: 'background 0.15s', whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = C.blueDark}
                                onMouseLeave={e => e.currentTarget.style.background = C.blue}
                            >
                                <ExternalLink size={11} /> View Course
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * LearningPathDetailPage
 *
 * Props:
 *   pathId (string)     – Path ID. Falls back to route param :id.
 *   pathData (object)   – Pre-loaded path (skips fetch).
 *   onBack (func)       – Back callback. Defaults to navigate(-1).
 */
const LearningPathDetailPage = ({ pathId: propPathId, pathData: propPathData, onBack }) => {
    const params = useParams();
    const navigate = useNavigate();

    const pathId = propPathId || params.id;
    const handleBack = onBack || (() => navigate(-1));

    const [path, setPath] = useState(propPathData || null);
    const [loading, setLoading] = useState(!propPathData);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (propPathData) return;
        if (!pathId) { setError('No learning path ID provided'); setLoading(false); return; }

        const fetchPath = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await trainingService.getLearningPathById(pathId);
                if (res.success) {
                    setPath(res.data);
                } else {
                    setError(res.error || 'Failed to load learning path details');
                }
            } catch {
                setError('Failed to load learning path details. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchPath();
    }, [pathId, propPathData]);

    // Derived
    const statusCfg = STATUS_CONFIG[path?.status] || STATUS_CONFIG.draft;
    const courses = path?.courses || [];
    const objectives = path?.learningObjectives || [];
    const skills = path?.skills || [];
    const tags = path?.tags || [];
    const durationMin = getPathDurationInMinutes(path);

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: `3px solid ${C.green100}`, borderTop: `3px solid ${C.green}`,
                    animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ fontSize: 13, color: C.gray500, fontWeight: 500 }}>Loading learning path details…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ── Error ────────────────────────────────────────────────────────────────
    if (error && !path) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertCircle size={24} color={C.red} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, margin: 0 }}>Failed to Load Learning Path</h3>
                <p style={{ fontSize: 13, color: C.gray500, textAlign: 'center', maxWidth: 340, margin: 0 }}>{error}</p>
                <button
                    onClick={handleBack}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 8,
                        padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: C.green, color: '#fff', fontSize: 13, fontWeight: 600,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.greenMid}
                    onMouseLeave={e => e.currentTarget.style.background = C.green}
                >
                    <ArrowLeft size={14} /> Go Back
                </button>
            </div>
        );
    }

    // ── Main Render ──────────────────────────────────────────────────────────
    return (
        <div style={{ minHeight: '100vh', background: C.gray50 }}>

            {/* ── Hero Header ──────────────────────────────────────────────── */}
            <div style={{ background: C.heroBg, padding: '0 0 28px', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative rings */}
                <div style={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: -130, right: -70, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', bottom: -70, left: 50, pointerEvents: 'none' }} />

                <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
                    {/* Back btn */}
                    <div style={{ paddingTop: 22, paddingBottom: 6 }}>
                        <button
                            onClick={handleBack}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                padding: '6px 14px', borderRadius: 7,
                                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                        >
                            <ArrowLeft size={13} /> Back to Learning Paths
                        </button>
                    </div>

                    {/* Breadcrumb label */}
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '14px 0 8px' }}>
                        Learning Path Details
                    </p>

                    {/* Status + category badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {path?.category && (
                            <span style={{
                                padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
                            }}>
                                {capitalizeValue(path.category)}
                            </span>
                        )}
                        <span style={{
                            padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: path?.status === 'published' ? C.green100 : C.gray100,
                            color: path?.status === 'published' ? C.greenMid : C.gray600,
                            border: `1px solid ${path?.status === 'published' ? C.green : C.gray300}40`,
                        }}>
                            {statusCfg.label}
                        </span>
                    </div>

                    <h1 style={{
                        fontSize: 'clamp(18px, 3vw, 30px)', fontWeight: 800, color: '#fff',
                        margin: '0 0 8px', lineHeight: 1.25, textTransform: 'capitalize', wordBreak: 'break-word',
                    }}>
                        {path?.title || 'Untitled Learning Path'}
                    </h1>

                    {path?.description && (
                        <p style={{
                            fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.75,
                            maxWidth: 680, margin: '0 0 20px', textTransform: 'capitalize', wordBreak: 'break-word',
                        }}>
                            {formatDescriptionText(path.description)}
                        </p>
                    )}

                    {/* Info badges grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10, maxWidth: 680 }}>
                        <HeroBadge icon={BookOpen} label="Courses" value={`${courses.length} course${courses.length !== 1 ? 's' : ''}`} />
                        {durationMin > 0 && (
                            <HeroBadge icon={Clock} label="Total Duration" value={formatDuration(durationMin)} />
                        )}
                        {path?.enrolledCount > 0 && (
                            <HeroBadge icon={Users} label="Enrolled" value={`${path.enrolledCount} learner${path.enrolledCount !== 1 ? 's' : ''}`} />
                        )}
                        {objectives.length > 0 && (
                            <HeroBadge icon={CheckCircle} label="Objectives" value={`${objectives.filter(o => (o || '').trim()).length} goal${objectives.length !== 1 ? 's' : ''}`} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 310px', gap: 24, alignItems: 'start' }}>

                    {/* Left – main content */}
                    <div>
                        {/* Soft error / warning */}
                        {error && (
                            <div style={{
                                marginBottom: 16, padding: '12px 16px', borderRadius: 9,
                                background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13,
                                display: 'flex', alignItems: 'flex-start', gap: 10,
                            }}>
                                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                                {error}
                            </div>
                        )}

                        {/* Description */}
                        {path?.description && (
                            <SectionCard title="About This Learning Path" icon={Info} accent={C.blue}>
                                <p style={{ fontSize: 13, color: C.gray700, lineHeight: 1.8, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                                    {formatDescriptionText(path.description)}
                                </p>
                            </SectionCard>
                        )}

                        {/* Learning Objectives */}
                        {objectives.length > 0 && objectives.some(o => (o || '').trim()) && (
                            <SectionCard title="Learning Objectives" icon={CheckCircle} accent={C.green}>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {objectives.filter(o => (o || '').trim()).map((obj, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                                                background: C.green100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <CheckCircle size={12} color={C.greenMid} />
                                            </div>
                                            <span style={{ fontSize: 13, color: C.gray700, textTransform: 'capitalize', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                {obj}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </SectionCard>
                        )}

                        {/* Courses */}
                        <SectionCard title="Courses in this Path" icon={BookOpen} accent={C.green}>
                            {courses.length > 0 ? (
                                <>
                                    {/* Summary bar */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                                        padding: '10px 14px', background: C.green50, borderRadius: 9,
                                        border: `1px solid ${C.green100}`,
                                    }}>
                                        <Layers size={14} color={C.greenMid} />
                                        <p style={{ fontSize: 13, fontWeight: 600, color: C.greenMid, margin: 0 }}>
                                            {courses.length} course{courses.length !== 1 ? 's' : ''}
                                            {durationMin > 0 && ` · ${formatDuration(durationMin)} total duration`}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {courses.map((entry, idx) => (
                                            <CourseCard
                                                key={(entry.course?._id || entry.course?.id || entry.id || idx)}
                                                courseEntry={entry}
                                                index={idx}
                                                onViewCourse={(id) => {
                                                    sessionStorage.setItem('trainingNavContext', 'training');
                                                    navigate(`/hrms/training/course/${id}`);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '28px 0', color: C.gray400 }}>
                                    <BookOpen size={32} style={{ marginBottom: 10, opacity: 0.35 }} />
                                    <p style={{ fontSize: 13, margin: 0 }}>No courses linked to this learning path yet.</p>
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Right – sidebar */}
                    <div style={{ position: 'sticky', top: 24 }}>
                        {/* Path Info card */}
                        <div style={{
                            background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 18,
                        }}>
                            <div style={{
                                padding: '14px 18px', borderBottom: `1px solid ${C.gray100}`,
                                background: C.gray50, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                <Info size={14} color={C.blue} />
                                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Path Info</h3>
                            </div>
                            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[
                                    { label: 'Category', value: path?.category ? capitalizeValue(path.category) : null },
                                    { label: 'Status', value: statusCfg.label },
                                    { label: 'Duration', value: durationMin > 0 ? formatDuration(durationMin) : null },
                                    { label: 'Completion', value: path?.completionCriteria ? capitalizeValue(path.completionCriteria) : null },
                                    { label: 'Max Students', value: path?.enrollment?.maxStudents },
                                    { label: 'Auto-Enroll', value: path?.enrollment?.autoEnrollment !== undefined ? (path.enrollment.autoEnrollment ? 'Yes' : 'No') : null },
                                    { label: 'Certificate', value: path?.certificate?.enabled ? 'Awarded on Pass' : null },
                                    {
                                        label: 'Created',
                                        value: path?.createdAt
                                            ? new Date(path.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : null,
                                    },
                                    {
                                        label: 'Updated',
                                        value: path?.updatedAt
                                            ? new Date(path.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : null,
                                    },
                                ].filter(item => item.value !== null && item.value !== undefined && String(item.value).trim() !== '').map(({ label, value }) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <span style={{ fontSize: 12, color: C.gray500, fontWeight: 500, flexShrink: 0 }}>{label}</span>
                                        <span style={{ fontSize: 12, color: C.gray800, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
                                            {String(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Skills & Tags */}
                        {(skills.length > 0 || tags.length > 0) && (
                            <div style={{
                                background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '14px 18px', marginBottom: 18,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <Tag size={13} color={C.blue} />
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Skills & Tags</h3>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {[...skills, ...tags].filter(Boolean).map((item, i) => (
                                        <span key={i} style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                            background: C.blue50, color: C.blue, border: `1px solid ${C.blue100}`,
                                        }}>
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Summary card */}
                        <div style={{
                            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                            borderRadius: 12, padding: '18px',
                            boxShadow: `0 8px 20px rgba(34,197,94,0.28)`,
                        }}>
                            <h3 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                Quick Summary
                            </h3>
                            {[
                                { label: 'Total Courses', value: courses.length, icon: BookMarked },
                                { label: 'Est. Duration', value: durationMin > 0 ? formatDuration(durationMin) : '—', icon: Clock },
                                { label: 'Objectives', value: objectives.filter(o => (o || '').trim()).length, icon: CheckCircle },
                                { label: 'Skills / Tags', value: [...skills, ...tags].filter(Boolean).length, icon: Tag },
                            ].map(({ label, value, icon: Icon }) => (
                                <div key={label} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.12)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon size={13} color="rgba(255,255,255,0.75)" />
                                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)' }}>{label}</span>
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LearningPathDetailPage;
